import { useEffect, useRef } from "react"
import Link from "next/link"

import { Button } from "@coval/ui"
import { focusManager } from "@tanstack/react-query"
import { ExternalLink } from "lucide-react"

/**
 * How many returns to this tab one trip to the creation form buys. Enough for
 * a user who comes back, goes again, and saves on the second visit; few enough
 * to bound what ordinary tab switching can cost afterwards. Opening the form
 * again starts the count over.
 */
const REVALIDATED_RETURNS_PER_OPEN = 3

interface PickerCreateButtonProps {
  /** Names the asset being created, e.g. "Create Agent". */
  label: string
  /** The asset's creation form — never its catalog, which is the picker. */
  href: string
  /**
   * Revalidates the catalog behind this picker. Called when this tab regains
   * focus after the creation form was opened, for the first
   * `REVALIDATED_RETURNS_PER_OPEN` returns.
   */
  onReturn?: () => void
  testId?: string
}

/**
 * The create action a picker offers at the leading edge of its footer, so a
 * user who has searched the catalog and found nothing to pick has somewhere to
 * go instead of a dead end.
 *
 * Opens in a new tab: a picker stands over work in progress — a launcher being
 * filled in, an agent's metrics being edited — and navigating away in place
 * loses both that work and the place in the catalog.
 *
 * The external-link icon says so on the face of it, since a new tab is not
 * what a button inside a dialog usually does.
 */
export function PickerCreateButton({
  label,
  href,
  onReturn,
  testId,
}: PickerCreateButtonProps) {
  // The creation form gets its own tab, so its query cache is not this tab's
  // and the invalidation it fires on success never reaches the catalog behind
  // this picker. Nothing revalidates that catalog on its own either: it is
  // held on the lookup cache profile, which leaves both mount and focus
  // refetching off. Without this, a user who creates the first asset and comes
  // back finds the same empty picker they left.
  //
  // Returns left to revalidate on. More than one, because the return that
  // matters is not always the first: a user is free to come back before
  // saving, save in the other tab, and come back again. Not unlimited, because
  // revalidating a catalog is not free — refetching every loaded page of a
  // large one takes real time — and a picker left open across an afternoon of
  // ordinary tab switching must not keep paying for it.
  const returnsLeft = useRef(0)

  useEffect(() => {
    if (!onReturn) {
      return
    }

    return focusManager.subscribe((focused) => {
      if (!focused || returnsLeft.current === 0) {
        return
      }
      returnsLeft.current -= 1
      onReturn()
    })
  }, [onReturn])

  const markCreationOpened = () => {
    returnsLeft.current = REVALIDATED_RETURNS_PER_OPEN
  }

  return (
    <Button variant="secondary" asChild data-testid={testId}>
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={markCreationOpened}
        onAuxClick={markCreationOpened}
      >
        {label}
        {/* The icon carries the new tab for the eye, the text for a screen
            reader, which is why the icon is hidden from it rather than
            announced twice. */}
        <ExternalLink className="size-3" aria-hidden="true" />
        <span className="sr-only"> (opens in a new tab)</span>
      </Link>
    </Button>
  )
}
